import { Flex, Spacing, Text } from '@billy/design-system'
import { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'

import type { FecImportItem } from '../../../../query-api'
import { formatFecImportPeriod } from '../../../../utils/formatFecImportPeriod'
import { getFecImportAnalysisErrorTranslation } from '../../../../utils/getFecImportAnalysisErrorTranslation'
import { getFecImportSessionFileSize } from '../../../../utils/getFecImportSessionFileSize'
import { isFecImportAnalysisComplete } from '../../../../utils/isFecImportAnalysisComplete'
import { FecImportFileOutput } from '../FecImportFileOutput'

interface FecImportOutputListProps {
  fecImports: FecImportItem[]
  gap?: Spacing
  overrideErrorMessageKey?: string
  sessionFiles: File[]
  title: string
}

export const FecImportOutputList = ({
  fecImports,
  gap = Spacing.S10,
  overrideErrorMessageKey,
  sessionFiles,
  title,
}: FecImportOutputListProps): ReactElement => {
  const { i18n, t } = useTranslation()

  return (
    <Flex flexDirection="column" gap={gap}>
      <Text colorVariant="primary" variant="h2">
        {title}
      </Text>
      {fecImports.map((fecImport) => {
        if (isFecImportAnalysisComplete(fecImport) && !overrideErrorMessageKey) {
          return (
            <FecImportFileOutput
              fileName={fecImport.fileName}
              key={fecImport.id}
              period={formatFecImportPeriod(fecImport.periodStart, fecImport.periodEnd, i18n.language)}
            />
          )
        }

        const errorTranslation = getFecImportAnalysisErrorTranslation(fecImport)
        const errorMessage = overrideErrorMessageKey
          ? t(overrideErrorMessageKey)
          : t(errorTranslation.messageKey, errorTranslation.interpolation)

        return (
          <FecImportFileOutput
            errorMessage={errorMessage}
            fileName={fecImport.fileName}
            fileSize={getFecImportSessionFileSize(fecImport, sessionFiles)}
            key={fecImport.id}
          />
        )
      })}
    </Flex>
  )
}
